import type { ScheduledRunInfo } from "features/adminUsers/adminUsers";

/** The zone the browser is in - what the operator reads the clock in. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** The booked moment in the viewer's own locale and zone, e.g. "12/09/2026, 09:00". */
export function formatScheduledRun(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

/** Short label for a tag: the date only when it is not today. */
export function scheduledRunTagLabel(run: ScheduledRunInfo): string {
  const date = new Date(run.scheduled_at);
  if (Number.isNaN(date.getTime())) return run.scheduled_at;
  const today = new Date();
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  const time = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (sameDay) return `Scheduled ${time}`;
  const day = date.toLocaleDateString([], { month: "short", day: "numeric" });
  return `Scheduled ${day}, ${time}`;
}

/** "in 4 hours" / "in 12 minutes" / "any moment now". */
export function timeUntil(iso: string): string {
  const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  if (Number.isNaN(seconds) || seconds <= 60) return "any moment now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `in ${hours} hour${hours === 1 ? "" : "s"}`;
  return `in ${Math.round(hours / 24)} days`;
}

/** What the tag's tooltip says: the whole story in one line. */
export function scheduledRunTooltip(run: ScheduledRunInfo): string {
  const who = run.scheduled_by ? ` by ${run.scheduled_by}` : "";
  const warned = run.reminder_sent_at
    ? "Slack has been told."
    : "Slack gets a heads-up 30 minutes before.";
  return (
    `Scheduled${who} for ${formatScheduledRun(run.scheduled_at)} ` +
    `(${timeUntil(run.scheduled_at)}). ${warned}`
  );
}
